import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import {
  traject,
  trajectPatch,
  zichtbaarSoort,
  type TrajectKind,
  type TrajectPatch,
} from '@/lib/traject';

type Optie = { id: string; label: string };

type Props = {
  projectId: string | null;
  chargingLocationId: string | null;
  chargingOpportunityId: string | null;
  /**
   * Krijgt altijd alle drie de kolommen, zodat er nooit twee tegelijk gevuld
   * raken. Bij een wissel naar een Project komt `projectNumber` mee, want dan
   * kan het offertenummer meeveranderen — zie de toelichting hieronder.
   */
  onChange: (patch: TrajectPatch, projectNumber?: string | null) => void;
  disabled?: boolean;
};

const SOORT: { value: TrajectKind; label: string; hint: string }[] = [
  { value: 'geen', label: 'Geen traject', hint: 'los aanbod' },
  { value: 'project', label: 'Greenvision-project', hint: 'klant investeert' },
  { value: 'kavel', label: 'Charging-kavel', hint: 'eigen risico' },
  { value: 'opportunity', label: 'Charging-opportunity', hint: 'vloot' },
];

/**
 * Tweetraps keuze van het traject: eerst het soort, dan de
 * entiteit. Dezelfde vorm als `DesignOwnerPicker` uit T2.1.
 *
 * Waarom niet drie losse keuzelijsten naast elkaar: dan kun je er twee vullen,
 * en dat weigert de database (`quotes_traject_check`). Door het soort eerst te
 * kiezen kan die situatie in de UI niet ontstaan, en is de CHECK alleen nog het
 * vangnet voor code die deze component omzeilt.
 *
 * **"Geen traject" is een geldige eindstand**, geen tussenstap: `createQuote`
 * verplicht wel een organisatie maar geen traject, dus een aanbod dat nog nergens
 * bij hoort is normaal.
 *
 * De klant-as (organisatie, contactpersoon) staat hier bewust níét in. Die blijft
 * gevuld ongeacht het traject — ook een Kavel- of Opportunity-offerte gaat naar
 * iemand toe. Zie de kop van `@/lib/traject`.
 */
export function TrajectPicker({
  projectId,
  chargingLocationId,
  chargingOpportunityId,
  onChange,
  disabled,
}: Props) {
  const huidig = traject({
    project_id: projectId,
    charging_location_id: chargingLocationId,
    charging_opportunity_id: chargingOpportunityId,
  });

  // Het soort dat net gekozen is, zolang er nog geen entiteit bij hoort. Zonder
  // dit leidt `traject()` uit drie lege kolommen "geen" af en springt de lijst terug.
  const [gekozenSoort, setGekozenSoort] = useState<TrajectKind | null>(null);
  const soort = zichtbaarSoort(huidig, gekozenSoort);

  const [projects, setProjects] = useState<Array<Optie & { project_number: string | null }>>([]);
  const [kavels, setKavels] = useState<Optie[]>([]);
  const [opportunities, setOpportunities] = useState<Optie[]>([]);

  useEffect(() => {
    const laden = async () => {
      const [p, k, o] = await Promise.all([
        supabase.from('projects').select('id, name, project_number').is('deleted_at', null).order('name'),
        supabase.from('charging_locations').select('id, name').is('deleted_at', null).order('name'),
        supabase.from('charging_opportunities').select('id, name').is('deleted_at', null).order('name'),
      ]);
      setProjects(
        (p.data ?? []).map((r: any) => ({
          id: r.id,
          label: r.project_number ? `${r.project_number} - ${r.name}` : r.name,
          project_number: r.project_number ?? null,
        }))
      );
      setKavels((k.data ?? []).map((r: any) => ({ id: r.id, label: r.name })));
      setOpportunities((o.data ?? []).map((r: any) => ({ id: r.id, label: r.name })));
    };
    laden();
  }, []);

  // Van soort wisselen leegt de andere twee kolommen meteen. Zou je de oude
  // waarde laten staan tot er een entiteit gekozen is, dan hangt de offerte
  // intussen nog aan het vorige traject.
  const kiesSoort = (nieuw: TrajectKind) => {
    if (nieuw === soort) return;
    setGekozenSoort(nieuw === 'geen' ? null : nieuw);
    onChange(trajectPatch(nieuw, null), null);
  };

  const kiesEntiteit = (wat: Exclude<TrajectKind, 'geen'>, id: string) => {
    const nummer =
      wat === 'project' ? (projects.find((p) => p.id === id)?.project_number ?? null) : null;
    onChange(trajectPatch(wat, id), nummer);
  };

  const opties: Optie[] =
    soort === 'project' ? projects
    : soort === 'kavel' ? kavels
    : soort === 'opportunity' ? opportunities
    : [];

  const placeholder =
    soort === 'project' ? 'Selecteer project'
    : soort === 'kavel' ? 'Selecteer kavel'
    : 'Selecteer opportunity';

  return (
    <div className="space-y-2">
      <Label htmlFor="traject-soort">Traject</Label>
      <Select
        value={soort}
        onValueChange={(v) => kiesSoort(v as TrajectKind)}
        disabled={disabled}
      >
        <SelectTrigger id="traject-soort">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SOORT.map((s) => (
            <SelectItem key={s.value} value={s.value}>
              <span className="flex w-full items-center gap-2">
                <span>{s.label}</span>
                <span className="text-[11px] text-muted-foreground">{s.hint}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {soort !== 'geen' && (
        <Select
          value={huidig.id ?? ''}
          onValueChange={(id) => kiesEntiteit(soort, id)}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {opties.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">Niets gevonden</div>
            ) : (
              opties.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.label}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
